import React, { createContext, useCallback, useContext, useState } from 'react';
import Icon from './Icon';

type ToastKind = 'default' | 'success' | 'error';

interface ToastState {
  id: number;
  message: string;
  kind: ToastKind;
}

interface ToastCtx {
  show: (message: string, kind?: ToastKind) => void;
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastCtx | null>(null);

export const useToast = (): ToastCtx => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const show = useCallback((message: string, kind: ToastKind = 'default') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, kind }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  const ctx: ToastCtx = {
    show,
    success: (m) => show(m, 'success'),
    error: (m) => show(m, 'error'),
  };

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <ToastStack toasts={toasts} />
    </ToastContext.Provider>
  );
};

const ToastStack: React.FC<{ toasts: ToastState[] }> = ({ toasts }) => {
  if (!toasts.length) return null;
  const toast = toasts[toasts.length - 1];

  return (
    <div className={`toast ${toast.kind === 'success' ? 'success' : toast.kind === 'error' ? 'error' : ''}`}>
      {toast.kind === 'success' && <Icon name="check" size={18} />}
      {toast.kind === 'error' && <Icon name="info" size={18} />}
      <span>{toast.message}</span>
    </div>
  );
};

export const useAsyncToast = () => {
  const toast = useToast();
  return useCallback(
    async <T,>(promise: Promise<T>, messages: { success?: string; error?: string }): Promise<T | null> => {
      try {
        const result = await promise;
        if (messages.success) toast.success(messages.success);
        return result;
      } catch (e) {
        toast.error(messages.error ?? 'Fehler');
        return null;
      }
    },
    [toast]
  );
};
